import React, { useEffect, useState } from 'react';
import { Check, ImagePlus, Loader2 } from 'lucide-react';
import { fetchSiteImports, fillImportImage, uploadPostImage, type Site, type SiteImport } from '@/services/siteApi';

// The move's photo to-do. Some hosts let us read the pages but not download the
// pictures; those pages still point at the old address, which breaks the day the
// old site goes away. Each missing image is a row: upload the photo and it takes
// that image's place on every page (a new preview version). Joe, Oct 25 2026:
// import everything but the images, then let the person add them in.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const IMAGE = /\.(jpe?g|png|gif|webp|avif|svg)(\?|$)/i;
const nameOf = (u: string) => { try { return decodeURIComponent(new URL(u).pathname.split('/').pop() || u); } catch { return u; } };

const SitePhotoTodo: React.FC<{ site: Site; onSite: (s: Site) => void; importJob?: SiteImport | null; compact?: boolean }> = ({ site, onSite, importJob = null, compact = false }) => {
  const [job, setJob] = useState<SiteImport | null>(importJob);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filledNow, setFilledNow] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (importJob) { setJob(importJob); return; }
    fetchSiteImports(site.id).then((r) => setJob(r.imports.find((i) => i.status === 'done') || null)).catch(() => {});
  }, [site.id, importJob?.id]);

  if (!job || !job.report) return null;
  const filled = new Set([...(job.filled || []), ...filledNow]);
  const missing = job.report.failed.filter((f) => (f as { kind?: string }).kind === 'image' || IMAGE.test(f.url));
  const todo = missing.filter((f) => !filled.has(f.url));
  if (!missing.length) return null;

  const fill = async (url: string, file: File) => {
    setBusy(url); setErr(null);
    try {
      const up = await uploadPostImage(site.id, file);
      const r = await fillImportImage(site.id, job.id, url, up.path);
      setFilledNow((s) => new Set(s).add(url));
      if (r.site) onSite(r.site);
    } catch (e) { setErr((e as Error).message); }
    setBusy(null);
  };

  return (
    <div className="flex flex-col" style={{ background: compact ? 'var(--hj-surface-1)' : '#fff', border: compact ? 0 : '1px solid var(--hj-hairline-soft)', borderRadius: compact ? 12 : 16, padding: compact ? 14 : 20, gap: 10 }}>
      <p className="m-0" style={{ fontSize: compact ? 14 : 16, fontWeight: 700, color: INK }}>
        {todo.length ? `Photos to add (${todo.length} of ${missing.length})` : 'All your photos are in'}
      </p>
      {todo.length > 0 && (
        <p className="m-0" style={{ fontSize: 14, color: MUTED, lineHeight: 1.45 }}>
          Your old host wouldn’t let us download {todo.length === 1 ? 'this picture' : 'these pictures'}. For now the pages still show {todo.length === 1 ? 'it' : 'them'} from your old site, which stops working once that site is gone. Upload each one and it goes in its place on every page.
        </p>
      )}
      <ul className="m-0 p-0" style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {missing.map((f) => {
          const done = filled.has(f.url);
          return (
            <li key={f.url} className="flex flex-wrap items-center" style={{ gap: 10 }}>
              <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: 14, color: INK, wordBreak: 'break-all' }}>
                <a href={f.url} target="_blank" rel="noreferrer" style={{ color: INK }}>{nameOf(f.url)}</a>
              </span>
              {done ? (
                <span className="inline-flex items-center" style={{ gap: 6, fontSize: 14, color: INK, fontWeight: 600 }}><Check size={16} aria-hidden="true" /> Added</span>
              ) : (
                <label className="inline-flex items-center" style={{ gap: 6, minHeight: 40, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--hj-hairline)', background: '#fff', color: INK, fontSize: 14, fontWeight: 500, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy && busy !== f.url ? 0.45 : 1 }}>
                  {busy === f.url ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus size={16} aria-hidden="true" />}
                  {busy === f.url ? 'Adding' : 'Upload photo'}
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden disabled={!!busy}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) fill(f.url, file); e.target.value = ''; }} />
                </label>
              )}
            </li>
          );
        })}
      </ul>
      {filledNow.size > 0 && <p className="m-0" style={{ fontSize: 13.5, color: MUTED }}>Added photos are on your private preview. Publish when you’re happy with it.</p>}
      {err && <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-pink-ink)' }}>{err}</p>}
    </div>
  );
};

export default SitePhotoTodo;
